import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconQuestionMark,
  IconX,
} from '@tabler/icons-react';
import { forwardRef, useState, useEffect } from 'react';
import { ModalAlert } from '../providers/alert-types';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface AlertModalProps {
  alert: ModalAlert;
  onResolve: (value?: any) => void;
  onReject: () => void;
}

const AlertModal = forwardRef<HTMLDivElement, AlertModalProps>(({ alert, onResolve, onReject }, ref) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [inputValue, setInputValue] = useState(alert.input?.defaultValue || '');
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState<string>('');
  const [countdown, setCountdown] = useState<number | null>(
    alert.duration && alert.duration > 0 ? Math.ceil(alert.duration / 1000) : null
  );

  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const typeConfig = {
    success: { color: theme.colors.green[6], icon: <IconCheck size={48} /> },
    error: { color: theme.colors.red[6], icon: <IconX size={48} /> },
    warning: { color: theme.colors.yellow[6], icon: <IconAlertTriangle size={48} /> },
    info: { color: theme.colors.blue[6], icon: <IconInfoCircle size={48} /> },
    confirm: { color: theme.colors.blue[6], icon: <IconQuestionMark size={48} /> },
    prompt: { color: theme.colors.blue[6], icon: <IconQuestionMark size={48} /> },
  };

  const config = typeConfig[alert.type];
  const gradient = alert.gradient && alert.gradientVariant ? theme.other.gradients[alert.gradientVariant] : null;

  // Countdown timer (updates every second)
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const interval = setInterval(() => {
        setCountdown((prev) => (prev !== null && prev > 0 ? prev - 1 : null));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [countdown]);

  // Auto-close timer
  useEffect(() => {
    if (alert.duration && alert.duration > 0) {
      const timer = setTimeout(() => {
        if (alert.type === 'confirm') {
          onReject(); // Auto-reject confirm dialogs
        } else {
          onResolve(); // Auto-resolve other types
        }
      }, alert.duration);

      return () => clearTimeout(timer);
    }
  }, [alert.duration, alert.type, onResolve, onReject]);

  const handleConfirm = async () => {
    // Validate input for prompt type
    if (alert.type === 'prompt' && alert.input?.validation) {
      const validationResult = alert.input.validation(inputValue);
      if (validationResult !== true) {
        setInputError(typeof validationResult === 'string' ? validationResult : 'Invalid input');
        return;
      }
    }

    setLoading(true);
    try {
      if (alert.onConfirm) {
        await alert.onConfirm(alert.type === 'prompt' ? inputValue : undefined);
      }

      // Resolve based on type
      if (alert.type === 'prompt') {
        onResolve(inputValue);
      } else if (alert.type === 'confirm') {
        onResolve(true);
      } else {
        onResolve();
      }
    } catch (error) {
      console.error('Alert confirmation error:', error);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (alert.onCancel) {
      alert.onCancel();
    }
    onReject();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: alert.withBlur ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.55)',
        backdropFilter: alert.withBlur ? 'blur(8px)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={alert.closeOnClickOutside !== false ? handleCancel : undefined}
    >
      <Box
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: isDark ? theme.colors.dark[7] : theme.white,
          padding: '2rem',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          color: isDark ? theme.colors.gray[2] : theme.colors.dark[7],
        }}
      >
      <Stack gap="lg" align="center" py="md">
        {/* Icon */}
        <Box
          style={{
            color: gradient ? 'white' : config.color,
            animation: 'alertBounce 0.5s ease-out',
          }}
        >
          {alert.icon || config.icon}
        </Box>

        {/* Title and Message */}
        <Stack gap="xs" align="center" style={{ width: '100%' }}>
          <Title order={3} ta="center">
            {alert.title}
          </Title>
          <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
            {alert.message}
          </Text>
          {/* Countdown Display */}
          {countdown !== null && countdown > 0 && (
            <Text size="xs" c="dimmed" ta="center" style={{ fontStyle: 'italic', ...accessibilityStyles }}>
              Auto-closing in {countdown} second{countdown !== 1 ? 's' : ''}...
            </Text>
          )}
        </Stack>

        {/* Prompt Input */}
        {alert.type === 'prompt' && (
          <Box style={{ width: '100%' }}>
            <TextInput
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setInputError('');
              }}
              placeholder={alert.input?.placeholder}
              error={inputError}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !inputError) {
                  handleConfirm();
                }
              }}
            />
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="center" gap="sm" w="100%">
          {(alert.type === 'confirm' || alert.type === 'prompt') && alert.cancelText && (
            <Button variant="subtle" onClick={handleCancel} disabled={loading}>
              {alert.cancelText}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            loading={loading}
            style={
              gradient
                ? {
                    backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                    color: 'white',
                  }
                : undefined
            }
            color={gradient ? undefined : config.color}
          >
            {alert.confirmText || 'OK'}
          </Button>
        </Group>
      </Stack>

      {/* Inline styles for bounce animation */}
      <style>
        {`
          @keyframes alertBounce {
            0% {
              transform: scale(0.3);
              opacity: 0;
            }
            50% {
              transform: scale(1.05);
            }
            70% {
              transform: scale(0.9);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </Box>
    </div>
  );
});

AlertModal.displayName = 'AlertModal';

export default AlertModal;
